import type { TeactNode } from '../../lib/teact/teact';
import React, { memo, useCallback, useEffect, useMemo, useRef } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type {
  ApiBaseCurrency, ApiFetchEstimateDieselResult, ApiNft, ApiTransferSponsorship,
} from '../../api/types';
import type { SavedAddress, UserToken } from '../../global/types';
import type { LangFn } from '../../hooks/useLang';
import type { ExplainedTransferFee } from '../../util/fee/transferFee';
import type { FeePrecision, FeeTerms } from '../../util/fee/types';
import { ScamWarningType, TransferState } from '../../global/types';

import { DEFAULT_PRICE_CURRENCY, IS_LEGENDS_WALLET } from '../../config';
import { getHelpCenterUrl } from '../../global/helpers/getHelpCenterUrl';
import {
  selectCurrentAccount,
  selectCurrentAccountId,
  selectCurrentAccountState,
  selectCurrentAccountTokenBalance,
  selectCurrentAccountTokens,
  selectHasMultipleAccounts,
  selectIsAllowSuspiciousActions,
  selectIsHardwareAccount,
  selectIsMultisigWallet,
} from '../../global/selectors';
import buildClassName from '../../util/buildClassName';
import { getChainConfig } from '../../util/chain';
import { SECOND } from '../../util/dateFormat';
import { toDecimal } from '../../util/decimals';
import { stopEvent } from '../../util/domEvents';
import { getMaxTransferAmount, isBalanceSufficientForTransfer } from '../../util/fee/transferFee';
import { vibrate } from '../../util/haptics';
import { isValidAddressOrDomain } from '../../util/isValidAddress';
import { debounce } from '../../util/schedulers';
import { trimStringByMaxBytes } from '../../util/text';
import { getChainBySlug, getIsNativeToken, getIsServiceToken, getNativeToken } from '../../util/tokens';

import useCurrentOrPrev from '../../hooks/useCurrentOrPrev';
import useFlag from '../../hooks/useFlag';
import useInterval from '../../hooks/useInterval';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import { useTransitionActiveKey } from '../../hooks/useTransitionActiveKey';
import { useAmountInputState } from '../ui/hooks/useAmountInputState';

import AccountSwitcherPill from '../common/AccountSwitcherPill';
import FeeDetailsModal from '../common/FeeDetailsModal';
import PrepaidTopupModal from '../prepaid/PrepaidTopupModal';
import AddressInput from '../ui/AddressInput';
import AmountInput from '../ui/AmountInput';
import Button from '../ui/Button';
import FeeLine from '../ui/FeeLine';
import Modal from '../ui/Modal';
import Transition from '../ui/Transition';
import CommentSection from './CommentSection';
import NftChips from './NftChips';
import NftInfo from './NftInfo';
import SentTabs from './SentTabs';

import modalStyles from '../ui/Modal.module.scss';
import styles from './Transfer.module.scss';

interface StateProps {
  toAddress?: string;
  resolvedAddress?: string;
  toAddressName?: string;
  amount?: bigint;
  comment?: string;
  shouldEncrypt?: boolean;
  isActive: boolean;
  isLoading?: boolean;
  isComplete?: boolean;
  tokenSlug: string;
  tokens?: UserToken[];
  savedAddresses?: SavedAddress[];
  nativeTokenBalance: bigint;
  isEncryptedCommentSupported: boolean;
  isMemoRequired?: boolean;
  baseCurrency: ApiBaseCurrency;
  nfts?: ApiNft[];
  binPayload?: string;
  stateInit?: string;
  diesel?: ApiFetchEstimateDieselResult;
  isDieselAuthorizationStarted?: boolean;
  isMultisig: boolean;
  isSensitiveDataHidden?: true;
  scamWarningType?: ScamWarningType;
  isAllowSuspiciousActions: boolean;
  isTransferReadonly?: boolean;
  explainedFee?: ExplainedTransferFee;
  sponsorship?: ApiTransferSponsorship;
  repeatRequired?: boolean;
  accountId?: string;
  accountTitle?: string;
  hasMultipleAccounts?: boolean;
}

const COMMENT_MAX_SIZE_BYTES = 5000;
const ACTIVE_STATES = new Set([TransferState.Initial, TransferState.None]);
const AUTHORIZE_DIESEL_INTERVAL_MS = SECOND;

const runDebounce = debounce((cb) => cb(), 500, false);

export function TransferInitial({
  tokenSlug,
  toAddress = '',
  resolvedAddress,
  toAddressName = '',
  amount,
  comment = '',
  shouldEncrypt,
  tokens,
  savedAddresses,
  nativeTokenBalance,
  isEncryptedCommentSupported,
  isMemoRequired,
  isActive,
  isLoading,
  isComplete,
  baseCurrency,
  nfts,
  binPayload,
  stateInit,
  diesel,
  isDieselAuthorizationStarted,
  isMultisig,
  isSensitiveDataHidden,
  scamWarningType,
  isAllowSuspiciousActions,
  isTransferReadonly,
  explainedFee,
  sponsorship,
  repeatRequired,
  accountId,
  accountTitle,
  hasMultipleAccounts,
}: StateProps) {
  const {
    submitTransferInitial,
    prefetchTransferQuote,
    fetchTransferFee,
    fetchNftFee,
    changeTransferToken,
    setTransferAmount,
    setTransferToAddress,
    setTransferComment,
    setTransferShouldEncrypt,
    cancelTransfer,
    showDialog,
    authorizeDiesel,
    fetchTransferDieselState,
    checkTransferAddress,
    dismissTransferScamWarning,
    setTransferScreen,
  } = getActions();

  const isNftTransfer = Boolean(nfts?.length);
  if (isNftTransfer) {
    const nftChain = nfts[0].chain;
    // Token and amount can't be selected in the NFT transfer form, so they are overwritten once for convenience
    tokenSlug = getChainConfig(nftChain).nativeToken.slug;
    amount = undefined;
  }

  const lang = useLang();

  const transferToken = useMemo(() => tokens?.find((token) => token.slug === tokenSlug), [tokenSlug, tokens]);
  const { amount: balance, symbol, chain } = transferToken || {};

  const renderedScamWarningType = useCurrentOrPrev(scamWarningType, true);
  const amountInputRef = useRef<HTMLInputElement>();
  const isDisabledDebounce = useRef<boolean>(false);
  const isAddressValid = chain ? isValidAddressOrDomain(toAddress, chain) : undefined;
  const doesSupportComment = chain && getChainConfig(chain).isTransferPayloadSupported;
  const doesSupportCommentEncryption = !!chain
    && getChainConfig(chain).isEncryptedCommentSupported
    && isEncryptedCommentSupported;
  const transitionKey = useTransitionActiveKey(nfts?.length ? nfts : [tokenSlug]);

  const handleAddressInput = useLastCallback((newToAddress?: string, isValueReplaced?: boolean) => {
    // If value is replaced, callbacks must be executed immediately, without debounce
    if (isValueReplaced) {
      isDisabledDebounce.current = true;
    }

    setTransferToAddress({ toAddress: newToAddress });
  });

  const handleAddressPaste = useLastCallback((address: string) => {
    if (!chain || !isValidAddressOrDomain(address, chain)) return;

    requestAnimationFrame(() => {
      amountInputRef.current?.focus();
    });
  });

  const safeExplainedFee = useMemo(() => {
    return explainedFee ?? {
      isGasless: false,
      canTransferFullBalance: false,
    };
  }, [explainedFee]);

  const balanceCheckFee = isLoading || sponsorship?.paymentMode === 'prepaid' || sponsorship?.paymentMode === 'none'
    ? undefined
    : sponsorship ? safeExplainedFee.realFee?.terms : safeExplainedFee.fullFee?.terms;

  // Note: this constant has 3 distinct meaningful values
  const isEnoughBalance = isBalanceSufficientForTransfer({
    tokenBalance: balance,
    nativeTokenBalance,
    transferAmount: isNftTransfer ? 0n : amount,
    fullFee: balanceCheckFee,
    canTransferFullBalance: safeExplainedFee.canTransferFullBalance,
  });

  const isAmountMissing = !isNftTransfer && !amount;

  const maxAmount = getMaxTransferAmount({
    tokenBalance: balance,
    tokenSlug,
    fullFee: safeExplainedFee.fullFee?.terms,
    canTransferFullBalance: safeExplainedFee.canTransferFullBalance,
  });

  const isDieselNotAuthorized = diesel?.status === 'not-authorized';
  const authorizeDieselInterval = isDieselNotAuthorized && isDieselAuthorizationStarted
    ? AUTHORIZE_DIESEL_INTERVAL_MS
    : undefined;

  const updateDieselState = useLastCallback(() => {
    fetchTransferDieselState({ tokenSlug });
  });

  useInterval(updateDieselState, authorizeDieselInterval);

  const fullFee = useMemo(() => {
    return getFullFee(safeExplainedFee.fullFee?.terms, tokenSlug);
  }, [safeExplainedFee.fullFee, tokenSlug]);

  useEffect(() => {
    if (
      balance && amount && fullFee !== undefined
      && amount <= balance
      && fullFee > 0n && fullFee < balance
      && amount + fullFee > balance
    ) {
      setTransferAmount({ amount: balance - fullFee });
    }
  }, [amount, balance, fullFee]);

  useEffect(() => {
    if (IS_LEGENDS_WALLET) {
      if (!isActive || isLoading || isNftTransfer || repeatRequired || isMultisig || !accountId
        || !isAddressValid || !amount || amount <= 0n || balance === undefined || amount > balance
        || (isMemoRequired && !comment)) return;

      const timeout = window.setTimeout(() => {
        prefetchTransferQuote({ tokenSlug, toAddress, amount, comment, shouldEncrypt, binPayload, stateInit });
      }, 700);
      return () => window.clearTimeout(timeout);
    }

    if (isAmountMissing || !isAddressValid) {
      return;
    }

    const runFunction = () => {
      if (isNftTransfer) {
        fetchNftFee({
          toAddress,
          comment,
          nfts: nfts ?? [],
        });
      } else {
        fetchTransferFee({
          tokenSlug,
          toAddress,
          amount,
          comment,
          shouldEncrypt,
          binPayload,
          stateInit,
        });
      }
    };

    if (!isDisabledDebounce.current) {
      runDebounce(runFunction);
    } else {
      isDisabledDebounce.current = false;
      runFunction();
    }
  }, [
    isAmountMissing, binPayload, comment, shouldEncrypt, isAddressValid, isNftTransfer, nfts, stateInit, toAddress,
    tokenSlug, amount, isActive, isLoading, repeatRequired, isMultisig, accountId, balance, isMemoRequired,
  ]);

  useEffect(() => {
    if (getIsServiceToken(transferToken)) {
      showDialog({
        title: lang('Warning!'),
        message: lang('$service_token_transfer_warning'),
        noBackdropClose: true,
      });
    }
  }, [lang, transferToken]);

  useEffect(() => {
    if (isComplete) clearForm();
  }, [isComplete]);

  const handleTokenChange = useLastCallback((slug: string) => {
    changeTransferToken({ tokenSlug: slug });
  });

  function clearForm() {
    handleAddressInput('');
    checkTransferAddress({});
    setTransferAmount({ amount: undefined });
    setTransferComment({ comment: undefined });
    setTransferShouldEncrypt({ shouldEncrypt: false });
  }

  const handleCloseClick = useLastCallback(() => {
    cancelTransfer({ shouldReset: true });
  });

  const handleOpenAccountSelector = useLastCallback(() => {
    setTransferScreen({ state: TransferState.SelectAccount });
  });

  const handleScamWarningModalClose = useLastCallback(() => {
    dismissTransferScamWarning();
    cancelTransfer({ shouldReset: true });
  });

  const handleAmountChange = (amount?: bigint, isValueReplaced?: boolean) => {
    // The amount input may change the amount when it's in the base currency mode and the token price changes.
    // Meanwhile, the amount in the global state must not change after the transfer form is submitted.
    if (!isActive) {
      return;
    }

    if (amount !== undefined && amount < 0) {
      return;
    }

    // If the value is replaced, callbacks must be executed immediately, without debounce
    if (isValueReplaced) {
      isDisabledDebounce.current = true;
    }

    setTransferAmount({ amount });
  };

  const handlePaste = useLastCallback(() => {
    isDisabledDebounce.current = true;
  });

  const handleCommentChange = useLastCallback((value) => {
    setTransferComment({ comment: trimStringByMaxBytes(value, COMMENT_MAX_SIZE_BYTES) });
  });

  const isAmountGreaterThanBalance = !isNftTransfer && balance !== undefined && amount !== undefined
    && amount > balance;
  const isFeeCoveredByBalance = sponsorship?.paymentMode === 'prepaid' || sponsorship?.paymentMode === 'none';
  const hasInsufficientFeeError = isEnoughBalance === false && !isAmountGreaterThanBalance
    && !isFeeCoveredByBalance
    && diesel?.status !== 'not-authorized' && diesel?.status !== 'pending-previous';
  const hasAmountError = !isNftTransfer && amount !== undefined && (
    (maxAmount !== undefined && amount > maxAmount)
    || hasInsufficientFeeError // Ideally, the insufficient fee error message should be displayed somewhere else
  );
  const isCommentRequired = Boolean(toAddress) && isMemoRequired;
  const hasCommentError = isCommentRequired && !comment;
  const isPrepaidInsufficient = Boolean(sponsorship?.isPrepaidInsufficient);

  const canSubmitWithoutQuote = IS_LEGENDS_WALLET && !isNftTransfer
    && balance !== undefined && amount !== undefined && amount > 0n && amount <= balance;
  const canSubmit = !repeatRequired && (isDieselNotAuthorized || Boolean(
    isAddressValid
    && !isAmountMissing && !hasAmountError
    && (canSubmitWithoutQuote || isEnoughBalance || (isFeeCoveredByBalance && !isAmountGreaterThanBalance))
    && !isPrepaidInsufficient
    && !hasCommentError
    && !isMultisig
    && (!safeExplainedFee.isGasless || diesel?.status === 'available' || diesel?.status === 'stars-fee')
    && !(isNftTransfer && !nfts?.length),
  ));
  const isContinueDisabled = Boolean(isLoading || (!repeatRequired && !isPrepaidInsufficient && !canSubmit));

  const [isRepeatModalOpen, openRepeatModal, closeRepeatModal] = useFlag(false);

  const handleSubmit = useLastCallback((e?: React.FormEvent | React.UIEvent) => {
    if (e) stopEvent(e);

    if (scamWarningType) return;

    if (repeatRequired) {
      openRepeatModal();
      return;
    }

    if (isDieselNotAuthorized) {
      authorizeDiesel();
      return;
    }

    if (!canSubmit) {
      return;
    }

    vibrate();

    submitTransferInitial({
      tokenSlug,
      amount: amount ?? 0n,
      toAddress,
      comment,
      binPayload,
      shouldEncrypt,
      nfts,
      isGasless: safeExplainedFee.isGasless,
      isGaslessWithStars: diesel?.status === 'stars-fee',
      stateInit,
    });
  });

  const [isFeeModalOpen, openFeeModal, closeFeeModal] = useFeeModal(safeExplainedFee);
  const [isTopupOpen, openTopup, closeTopup] = useFlag(false);

  const handleConfirmRepeat = useLastCallback(() => {
    closeRepeatModal();
    if (!amount || !isAddressValid || isNftTransfer) return;

    vibrate();
    submitTransferInitial({
      tokenSlug,
      toAddress,
      amount,
      comment,
      shouldEncrypt,
      binPayload,
      isGasless: safeExplainedFee.isGasless,
      isGaslessWithStars: diesel?.status === 'stars-fee',
      stateInit,
      allowRepeat: true,
    });
  });

  const handleTopupSuccess = useLastCallback(() => {
    if (!amount || !isAddressValid || isNftTransfer) return;
    const quotePayload = {
      tokenSlug,
      toAddress,
      amount,
      comment,
      shouldEncrypt,
      binPayload,
      stateInit,
    };
    if (IS_LEGENDS_WALLET) {
      submitTransferInitial(quotePayload);
    } else {
      fetchTransferFee(quotePayload);
    }
  });

  const tokensToSelect = useMemo(
    () => (tokens ?? []).filter((token) => isSelectableToken(token, tokenSlug)),
    [tokens, tokenSlug],
  );

  const amountInputProps = useAmountInputState({
    amount,
    token: transferToken,
    baseCurrency,
    isAmountReadonly: isTransferReadonly,
    onAmountChange: handleAmountChange,
    onTokenChange: handleTokenChange,
  });

  // It is necessary to use useCallback instead of useLastCallback here
  const renderBottomRight = useCallback((className?: string) => {
    let transitionKey = 0;
    let content: TeactNode = ' ';

    if (isMultisig) {
      transitionKey = 1;
      content = <span className={styles.balanceError}>{lang('Multisig sending disabled')}</span>;
    } else if (amount) {
      if (isAmountGreaterThanBalance) {
        transitionKey = 2;
        content = <span className={styles.balanceError}>{lang('Insufficient balance')}</span>;
      } else if (isPrepaidInsufficient) {
        transitionKey = 3;
        content = <span className={styles.balanceError}>{lang('Prepaid balance is insufficient')}</span>;
      } else if (hasInsufficientFeeError) {
        transitionKey = 3;
        content = <span className={styles.balanceError}>{lang('Insufficient fee')}</span>;
      }
    }

    return (
      <Transition
        className={className}
        name="fade"
        activeKey={transitionKey}
      >
        {content}
      </Transition>
    );
  }, [amount, hasInsufficientFeeError, isAmountGreaterThanBalance, isMultisig, isPrepaidInsufficient, lang]);

  function renderButtonText() {
    if (repeatRequired) return lang('$wallet_sponsorship_repeat_review');
    if (isPrepaidInsufficient) return lang('Top Up');
    if (diesel?.status === 'not-authorized') {
      return lang('Authorize %token% Fee', { token: symbol! });
    }
    if (diesel?.status === 'pending-previous') {
      return lang('Awaiting Previous Fee');
    }
    if (isAmountGreaterThanBalance) return lang('Insufficient balance');
    if (!isAmountMissing && hasInsufficientFeeError) return lang('Insufficient fee');
    return lang('Continue');
  }

  function renderFee() {
    let terms: FeeTerms | undefined;
    let precision: FeePrecision = 'exact';

    if (!isAmountMissing) {
      const actualFee = hasInsufficientFeeError && !sponsorship
        ? safeExplainedFee.fullFee
        : safeExplainedFee.realFee;
      if (actualFee) {
        ({ terms, precision } = actualFee);
      }
    }

    return (
      <FeeLine
        terms={terms}
        token={transferToken}
        precision={precision}
        onDetailsClick={IS_LEGENDS_WALLET ? undefined : openFeeModal}
      />
    );
  }

  return (
    <>
      <form
        className={modalStyles.transitionContent}
        onSubmit={handleSubmit}
        onPaste={handlePaste}
      >
        <Transition
          activeKey={transitionKey}
          name="semiFade"
          shouldCleanup
          slideClassName={styles.formSlide}
        >
          {hasMultipleAccounts && !isNftTransfer && accountId && (
            <AccountSwitcherPill
              accountId={accountId}
              title={accountTitle}
              className={styles.accountPill}
              onClick={handleOpenAccountSelector}
            />
          )}

          <Button
            isRound
            className={buildClassName(modalStyles.closeButton, styles.closeButton)}
            ariaLabel={lang('Close')}
            onClick={handleCloseClick}
          >
            <i className={buildClassName(modalStyles.closeIcon, 'icon-close')} aria-hidden />
          </Button>

          {isNftTransfer ? (
            <div className={styles.transferTitle}>
              {lang(nfts.length > 1 ? 'Send Collectibles' : 'Send Collectible')}
            </div>
          ) : (
            <SentTabs className={buildClassName(hasMultipleAccounts && styles.sentTabsWithSwitcher)} />
          )}

          {nfts?.length === 1 && <NftInfo nft={nfts[0]} withMediaViewer />}
          {Boolean(nfts?.length) && nfts.length > 1 && <NftChips nfts={nfts} />}

          <AddressInput
            label={lang('Recipient Address')}
            value={toAddress}
            chain={chain}
            // NFT transfers are available only on the TON blockchain on this moment
            addressBookChain={chain}
            savedAddresses={savedAddresses}
            shouldHideLocalAddresses
            validateAddress={checkTransferAddress}
            isReadonly={isTransferReadonly}
            withQrScan
            address={resolvedAddress || toAddress}
            addressName={toAddressName}
            onInput={handleAddressInput}
            onPaste={handleAddressPaste}
            onClose={cancelTransfer}
          />

          {!isNftTransfer && (
            <AmountInput
              {...amountInputProps}
              ref={amountInputRef}
              maxAmount={maxAmount}
              token={transferToken}
              allTokens={tokensToSelect}
              hasError={hasAmountError}
              withChainIcon
              isMaxAmountLoading={maxAmount === undefined}
              isSensitiveDataHidden={isSensitiveDataHidden}
              renderBottomRight={renderBottomRight}
              onPressEnter={handleSubmit}
            />
          )}

          {doesSupportComment && (
            <CommentSection
              comment={comment}
              shouldEncrypt={shouldEncrypt}
              binPayload={binPayload}
              stateInit={stateInit}
              chain={chain}
              isReadonly={isTransferReadonly}
              isCommentRequired={isCommentRequired}
              isEncryptedCommentSupported={doesSupportCommentEncryption}
              onCommentChange={handleCommentChange}
            />
          )}

          <div className={styles.footer}>
            {repeatRequired && (
              <div className={styles.error}>{lang('$wallet_sponsorship_already_sent')}</div>
            )}
            {!IS_LEGENDS_WALLET && renderFee()}

            <Button
              isPrimary={!isContinueDisabled}
              isSubmit={!isPrepaidInsufficient && !repeatRequired}
              isDisabled={isContinueDisabled}
              isLoading={isLoading}
              className={buildClassName(styles.continueButton, isLoading && styles.continueButtonLoading)}
              onClick={repeatRequired ? openRepeatModal : isPrepaidInsufficient ? openTopup : undefined}
            >
              {renderButtonText()}
            </Button>
          </div>
        </Transition>
      </form>
      <FeeDetailsModal
        isOpen={isFeeModalOpen}
        onClose={closeFeeModal}
        fullFee={safeExplainedFee.fullFee?.terms}
        realFee={safeExplainedFee.realFee?.terms}
        realFeePrecision={safeExplainedFee.realFee?.precision}
        excessFee={safeExplainedFee.excessFee}
        excessFeePrecision="approximate"
        token={transferToken}
      />
      {accountId && (
        <PrepaidTopupModal
          isOpen={isTopupOpen}
          accountId={accountId}
          onClose={closeTopup}
          onSuccess={handleTopupSuccess}
        />
      )}
      <Modal
        isOpen={isRepeatModalOpen}
        isCompact
        title={lang('$wallet_sponsorship_repeat_title')}
        onClose={closeRepeatModal}
      >
        <div className={styles.repeatDescription}>{lang('$wallet_sponsorship_repeat_confirm')}</div>
        <div className={styles.repeatDetails}>
          <strong className={styles.repeatAmount}>
            {toDecimal(amount ?? 0n, transferToken?.decimals)} {symbol}
          </strong>
          <span className={styles.repeatRecipientLabel}>{lang('Recipient')}</span>
          <code className={styles.repeatAddress}>{toAddress}</code>
        </div>
        <div className={buildClassName(modalStyles.footerButtons, styles.repeatActions)}>
          <Button className={modalStyles.button} onClick={closeRepeatModal}>{lang('Cancel')}</Button>
          <Button isPrimary className={modalStyles.button} onClick={handleConfirmRepeat}>
            {lang('$wallet_sponsorship_repeat_action')}
          </Button>
        </div>
      </Modal>
      <Modal
        isOpen={Boolean(scamWarningType)}
        isCompact
        title={lang('Warning!')}
        noBackdropClose
        onClose={handleScamWarningModalClose}
      >
        <div>
          {getScamWarning(lang, renderedScamWarningType)}
        </div>
        <div className={modalStyles.footerButtons}>
          {isAllowSuspiciousActions ? (
            <>
              <Button
                className={modalStyles.button}
                onClick={handleScamWarningModalClose}
              >
                {lang('Close')}
              </Button>
              <Button
                isPrimary
                isDestructive
                className={modalStyles.button}
                onClick={dismissTransferScamWarning}
              >
                {lang('Continue')}
              </Button>
            </>
          ) : (
            <Button onClick={handleScamWarningModalClose}>{lang('Close')}</Button>
          )}
        </div>
      </Modal>
    </>
  );
}

export default memo(
  withGlobal(
    (global): StateProps => {
      const {
        toAddress,
        resolvedAddress,
        toAddressName,
        amount,
        comment,
        shouldEncrypt,
        tokenSlug,
        isLoading,
        state,
        nfts,
        binPayload,
        isMemoRequired,
        diesel,
        stateInit,
        scamWarningType,
        isTransferReadonly,
        explainedFee,
        sponsorship,
        repeatRequired,
      } = global.currentTransfer;

      const isLedger = selectIsHardwareAccount(global);
      const currentAccountId = selectCurrentAccountId(global)!;
      const currentAccount = selectCurrentAccount(global);
      const accountState = selectCurrentAccountState(global);
      const { baseCurrency = DEFAULT_PRICE_CURRENCY, isSensitiveDataHidden } = global.settings;
      const isActive = ACTIVE_STATES.has(state);

      const chain = getChainBySlug(tokenSlug);
      return {
        toAddress,
        resolvedAddress,
        toAddressName,
        amount,
        comment,
        shouldEncrypt,
        nfts,
        tokenSlug,
        binPayload,
        stateInit,
        tokens: selectCurrentAccountTokens(global),
        savedAddresses: accountState?.savedAddresses,
        isEncryptedCommentSupported: !isLedger && !nfts?.length && !isMemoRequired,
        isMemoRequired,
        isActive,
        isLoading: isLoading && isActive,
        isComplete: state === TransferState.Complete,
        baseCurrency,
        nativeTokenBalance: selectCurrentAccountTokenBalance(global, getNativeToken(chain).slug),
        diesel,
        isDieselAuthorizationStarted: accountState?.isDieselAuthorizationStarted,
        isMultisig: selectIsMultisigWallet(global, currentAccountId, chain),
        isSensitiveDataHidden,
        scamWarningType,
        isAllowSuspiciousActions: selectIsAllowSuspiciousActions(global, currentAccountId),
        isTransferReadonly,
        explainedFee,
        sponsorship,
        repeatRequired,
        accountId: currentAccountId,
        accountTitle: currentAccount?.title,
        hasMultipleAccounts: selectHasMultipleAccounts(global),
      };
    },
    (global, _, stickToFirst) => stickToFirst(selectCurrentAccountId(global)),
  )(TransferInitial),
);

function useFeeModal(explainedFee: ExplainedTransferFee) {
  const isAvailable = explainedFee.realFee?.precision !== 'exact';
  const [isOpen, open, close] = useFlag(false);
  const openIfAvailable = isAvailable ? open : undefined;
  return [isOpen, openIfAvailable, close] as const;
}

function isSelectableToken(token: UserToken, selectedTokenSlug: string) {
  return token.type !== 'lp_token'
    || (token.amount > 0 && !token.isDisabled)
    || token.slug === selectedTokenSlug;
}

function getScamWarning(lang: LangFn, scamWarning: ScamWarningType | undefined) {
  if (!scamWarning) return undefined;

  return lang(scamWarning === ScamWarningType.DomainLike
    ? '$domain_like_scam_warning'
    : '$seed_phrase_scam_warning', {
    help_center_link: (
      <a
        href={getHelpCenterUrl(lang.code, scamWarning === ScamWarningType.DomainLike ? 'domainScam' : 'seedScam')}
        target="_blank"
        rel="noreferrer"
      >
        <b>{lang('$help_center_prepositional')}</b>
      </a>
    ),
  });
}

// Calculates fee in the token currency to keep abstraction from SDK native fee values
function getFullFee(terms: FeeTerms<bigint> | undefined, tokenSlug: string): bigint | undefined {
  if (!terms) return undefined;
  const tokenPart = terms.token ?? 0n;
  const nativePart = getIsNativeToken(tokenSlug) ? (terms.native ?? 0n) : 0n;
  return tokenPart + nativePart;
}
